// src/components/Topbar.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  FaBell,
  FaLock,
  FaUser,
  FaUserCircle,
  FaUsersCog,
  FaRupeeSign,
  FaFileAlt,
  FaComment,
  FaWhatsapp,
} from "react-icons/fa";
import { LuScanSearch } from "react-icons/lu";

import {
  FiVolume2,
  FiVolumeX,
  FiBell as INotif,
  FiPackage as IPkg,
  FiShoppingCart as ICart,
  FiAlertTriangle as IAlert,
  FiMessageSquare as IMsg,
  FiSun as ISun,
  FiDollarSign as IDollar,
  FiChevronDown,
  FiMinimize,
  FiMenu,
  FiX,
  FiChevronRight,
  FiLoader,
} from "react-icons/fi";

import { GiWallet } from "react-icons/gi";

import { motion, AnimatePresence } from "framer-motion";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoMdExpand } from "react-icons/io";
import { IoPricetags, IoSparklesSharp } from "react-icons/io5";

import {
  useGetNotificationFeedQuery,
  useMarkAllReadMutation,
  useMarkReadMutation,
} from "../toolkitServices/notificationsApi";
import {
  setFeed,
  markAllReadLocal,
  markReadLocal,
} from "../slices/notificationsSlice";
import toast from "react-hot-toast";
import useSwMessages from "../hooks/useSwMessages";
import useNotificationsSocket from "../hooks/useNotificationsSocket";
import { setScreenType } from "../slices/globalVariableSlice";
import { useLogoutUserMutation } from "../toolkitServices/userApi";
import { clearUser } from "../slices/authSlice";
import { renderHighlightWord, twoDecimal } from "../config/helperFunctions";

import ConfirmDialog from "./ConfirmDialog";
import { useGenerateSecurityPinMutation } from "../toolkitServices/securityPinApi";
import { FaBullhorn } from "react-icons/fa6";

// --- small helper: choose an icon + color by content ---
function pickNotifIcon(item) {
  const t = `${item?.name || ""} ${item?.action || ""}`.toLowerCase();
  let Icon = INotif;
  let bg = "from-violet-100 to-violet-200 text-violet-700";
  if (/quote|daily/i.test(t)) {
    Icon = ISun;
    bg = "from-amber-100 to-amber-200 text-amber-700";
  } else if (/sale|invoice|order|bill|payment/i.test(t)) {
    Icon = ICart;
    bg = "from-emerald-100 to-emerald-200 text-emerald-700";
  } else if (/stock|inventory|bucket|labelled|unlabelled/i.test(t)) {
    Icon = IPkg;
    bg = "from-sky-100 to-sky-200 text-sky-700";
  } else if (/alert|error|low|fail|warning/i.test(t)) {
    Icon = IAlert;
    bg = "from-rose-100 to-rose-200 text-rose-700";
  } else if (/message|comment|reply/i.test(t)) {
    Icon = IMsg;
    bg = "from-indigo-100 to-indigo-200 text-indigo-700";
  } else if (/price|amount|mrp/i.test(t)) {
    Icon = IDollar;
    bg = "from-green-100 to-green-200 text-green-700";
  }
  return { Icon, bg };
}

/* ---------- Dropdown Pill (submenu) ---------- */
function DropdownPill({
  item,
  isActive,
  onOpen,
  open,
  idx,
  registerActiveRef,
}) {
  const { pathname } = useLocation();
  const ref = useRef(null);
  const btnRef = useRef(null);

  // close on outside click/ESC
  useEffect(() => {
    const onDoc = (e) => {
      if (!open) return;
      if (e.key === "Escape") onOpen(-1);
      if (
        e.type === "mousedown" &&
        ref.current &&
        !ref.current.contains(e.target)
      ) {
        onOpen(-1);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onDoc);
    };
  }, [open, onOpen]);

  // make parent focus target when a child route is active
  useEffect(() => {
    if (isActive) registerActiveRef(btnRef.current);
  }, [isActive, registerActiveRef]);

  return (
    <div ref={ref} className="relative z-[99999] overflow-visible">
      <button
        ref={btnRef}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen(open ? -1 : idx);
        }}
        className={[
          "cursor-pointer px-3 py-1 text-xs rounded-md transition outline-none",
          "focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#9481ce]",
          "hover:bg-white/20",
          isActive
            ? "bg-white text-[#4b3d91] font-semibold shadow-sm"
            : "text-white/95",
          "flex items-center gap-1",
        ].join(" ")}
      >
        <span className="whitespace-nowrap">{item.title}</span>
        <FiChevronDown className={`transition ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            onMouseDown={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 6, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="absolute left-0 top-full z-[99999] mt-1 min-w-[220px] rounded-md bg-white shadow-lg ring-1 ring-violet-300 overflow-hidden"
          >
            {item.children.map((child) => {
              const active = pathname.startsWith(child.link);
              return (
                <NavLink
                  key={child.link}
                  to={child.link}
                  className={({ isActive: navActive }) =>
                    [
                      "block px-3 py-2 text-sm",
                      "hover:bg-violet-50",
                      navActive || active
                        ? "text-violet-700 font-semibold"
                        : "text-gray-700",
                    ].join(" ")
                  }
                  onClick={() => onOpen(-1)}
                >
                  {child.title}
                </NavLink>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Topbar({ menu = [] }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [logoutUser] = useLogoutUserMutation();
  const screenType = useSelector((s) => s.globalVariables.screenType);

  const auth = useSelector((s) => s.auth);
  const user = auth?.user;
  const token = auth?.token;

  // ✅ commission amount
  const commissionRaw =
    user?.employeeCommission ??
    user?.commission ??
    user?.walletAmount ??
    user?.wallet ??
    0;
  const commission = Number(commissionRaw || 0) || 0;

  // realtime
  useSwMessages?.();
  useNotificationsSocket({ userId: user?.userId || user?._id, token });

  const [showMenu, setShowMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // ✅ mobile burger menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenParent, setMobileOpenParent] = useState(-1);

  // ✅ confirm redeem
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);

  const activeBtnRef = useRef(null);
  const [openIdx, setOpenIdx] = useState(-1);

  // ✅ Profile dropdown refs
  const menuRef = useRef(null);
  const avatarBtnRef = useRef(null);

  // Close profile dropdown on outside click/esc
  useEffect(() => {
    const onDoc = (e) => {
      if (!showMenu) return;
      if (e.key === "Escape") setShowMenu(false);
      if (e.type === "mousedown") {
        const insideMenu = menuRef.current?.contains(e.target);
        const insideBtn = avatarBtnRef.current?.contains(e.target);
        if (!insideMenu && !insideBtn) setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onDoc);
    };
  }, [showMenu]);

  // Close mobile menu on Esc
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        setMobileOpenParent(-1);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // ---------- sound ----------
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const SOUND_SRC = (process.env.PUBLIC_URL || "") + "/sounds/notify.wav";

  const [soundOn, setSoundOn] = useState(
    () => (localStorage.getItem("notifSound") || "on") !== "off",
  );
  const [soundUnlocked, setSoundUnlocked] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [showEnableHint, setShowEnableHint] = useState(false);

  const warmedRef = useRef(false);
  const hydratedRef = useRef(false);
  const prevUnreadRef = useRef(0);

  const notifications = useSelector((s) => s.notifications.items);
  const unreadCount = useSelector((s) => s.notifications.unreadCount);

  const prevLenRef = useRef(0);
  const prevTopIdRef = useRef(null);

  const dingLockRef = useRef(false);
  const lastPlayTsRef = useRef(0);
  const useFallbackRef = useRef(false);
  const [generatePin, { isLoading: isGeneratingPin }] =
    useGenerateSecurityPinMutation();
  const { data: feed, refetch } = useGetNotificationFeedQuery({ limit: 20 });
  const [markAllRead] = useMarkAllReadMutation();
  const [markRead] = useMarkReadMutation();
  const [isFs, setIsFs] = useState(() => !!document.fullscreenElement);

  useEffect(() => {
    const onFsChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const handleExpandToggle = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        dispatch(setScreenType("expanded"));
      } else {
        await document.exitFullscreen();
        dispatch(setScreenType("normal"));
      }
    } catch (e) {
      console.error("Fullscreen failed:", e);
      dispatch(setScreenType("expanded"));
    }
  };

  useEffect(() => {
    const unlock = async () => {
      try {
        if (audioRef.current && !warmedRef.current) {
          try {
            audioRef.current.src = SOUND_SRC;
            const p = audioRef.current.play();
            if (p?.catch) await p.catch(() => {});
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          } catch {}
        }
        if (!audioCtxRef.current) {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          audioCtxRef.current = new Ctx();
        }
        await audioCtxRef.current.resume();
        warmedRef.current = true;
        setSoundUnlocked(true);
        setShowEnableHint(false);
        audioRef.current?.load?.();
      } catch {
        setShowEnableHint(true);
      }
    };
    const opts = { once: true, capture: true };
    ["pointerdown", "keydown", "touchstart"].forEach((e) =>
      document.addEventListener(e, unlock, opts),
    );
    return () =>
      ["pointerdown", "keydown", "touchstart"].forEach((e) =>
        document.removeEventListener(e, unlock, { capture: true }),
      );
  }, [SOUND_SRC]);

  useEffect(() => {
    prevUnreadRef.current = unreadCount || 0;
    prevLenRef.current = notifications.length || 0;
    prevTopIdRef.current = notifications[0]?._id || null;
  }, []); // eslint-disable-line

  useEffect(() => {
    if (feed?.success) {
      const rows = (feed.data || []).map((r) => ({
        _id: r._id,
        name: r.title,
        action: r.body,
        time: new Date(r.createdAt).toLocaleString(),
        avatar: r.icon || "/icons/icon-192.png",
        unread: !!r.unread,
        url: r.url || "/",
      }));
      dispatch(setFeed({ items: rows, unreadCount: feed.unreadCount || 0 }));
      if (!hydratedRef.current) {
        hydratedRef.current = true;
        prevUnreadRef.current = feed.unreadCount || 0;
        prevLenRef.current = rows.length;
        prevTopIdRef.current = rows[0]?._id || null;
      }
    }
  }, [feed, dispatch]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (unreadCount > prevUnreadRef.current) playDing();
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (notifications.length > prevLenRef.current) playDing();
    prevLenRef.current = notifications.length;
  }, [notifications.length]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    const topId = notifications[0]?._id || null;
    if (topId && topId !== prevTopIdRef.current) playDing();
    prevTopIdRef.current = topId;
  }, [notifications]);

  const playDing = async () => {
    if (!soundOn) return;
    const now = Date.now();
    if (dingLockRef.current) return;
    if (now - lastPlayTsRef.current < 700) return;

    dingLockRef.current = true;
    try {
      let ok = false;
      if (audioRef.current && soundUnlocked) {
        try {
          const el = audioRef.current;
          if (!el.src) el.src = SOUND_SRC;
          if (!audioReady) el.load();
          el.pause();
          el.currentTime = 0;
          el.volume = 1.0;
          await el.play();
          ok = true;
        } catch {
          ok = false;
        }
      }
      if (!ok && useFallbackRef.current) {
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          let ctx = audioCtxRef.current || new Ctx();
          audioCtxRef.current = ctx;
          await ctx.resume();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine";
          o.frequency.value = 880;
          o.connect(g);
          g.connect(ctx.destination);
          const t = ctx.currentTime;
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(0.22, t + 0.01);
          g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
          o.start(t);
          o.stop(t + 0.25);
        } catch {
          setShowEnableHint(true);
        }
      }
    } finally {
      lastPlayTsRef.current = Date.now();
      dingLockRef.current = false;
    }
  };

  // focus active pill
  useEffect(() => {
    document.activeElement?.blur?.();
    const id = requestAnimationFrame(() => {
      activeBtnRef.current?.focus?.({ preventScroll: false });
    });
    return () => cancelAnimationFrame(id);
  }, [pathname, menu]);

  const openDropdown = () => {
    setShowNotifications((s) => !s);
    if (!showNotifications) refetch();
  };

  const unreadCountSel = useSelector((s) => s.notifications.unreadCount);
  const badge = useMemo(
    () => (unreadCountSel > 9 ? "9+" : `${unreadCountSel}`),
    [unreadCountSel],
  );

  const onGenerate = async () => {
    try {
      const res = await generatePin().unwrap();
      const msg =
        res?.msg ||
        res?.message ||
        res?.data?.msg ||
        "Security PIN generated & sent to owner mobile.";
      toast.success(msg);
    } catch (e) {
      const raw =
        e?.data?.msg ||
        e?.data?.message ||
        e?.data?.raw ||
        e?.error ||
        "Failed to generate PIN";
      // HTML/raw text ko short karo
      const safeMsg =
        typeof raw === "string" ? raw.slice(0, 180) : "Failed to generate PIN";
      toast.error(safeMsg);
    }
  };

  // utility: does any child match current path?
  const parentActive = (children = []) =>
    children.some((c) => pathname.startsWith(c.link));

  const handleConfirmRedeem = async () => {
    try {
      setRedeemLoading(true);
      setShowMenu(false);
      setRedeemOpen(false);
      navigate("/commission/redeem");
    } finally {
      setRedeemLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="flex justify-between items-center px-2 sm:px-4 py-2 bg-transparent relative z-[99999] overflow-visible"
    >
      {/* ✅ Confirm dialog for redeem */}
      <ConfirmDialog
        open={redeemOpen}
        onCancel={() => (!redeemLoading ? setRedeemOpen(false) : null)}
        onConfirm={handleConfirmRedeem}
        title="Send Redeem Request?"
        message="Are you sure you want to send redeem request?"
        confirmText="Yes, Send"
        cancelText="Cancel"
        loading={redeemLoading}
        icon={
          <div className="h-10 w-10 rounded-full bg-violet-100 text-violet-700 grid place-items-center">
            <GiWallet className="h-5 w-5" />
          </div>
        }
      />

      {/* hidden audio */}
      <audio
        ref={audioRef}
        preload="auto"
        src={SOUND_SRC}
        onCanPlayThrough={() => setAudioReady(true)}
        onError={() => setAudioReady(false)}
      />

      {/* ✅ Left: Mobile burger + Desktop pills */}
      <div className="flex items-center gap-2">
        {/* Mobile burger */}
        <button
          type="button"
          onClick={() => {
            setMobileMenuOpen(true);
            setMobileOpenParent(-1);
          }}
          className="sm:hidden inline-flex items-center justify-center h-10 w-10 rounded-full
                     bg-[#9481ce] text-white shadow-md active:scale-[0.98]"
          title="Open menu"
          aria-label="Open menu"
        >
          <FiMenu size={18} />
        </button>

        {/* ✅ Desktop/Tab Pills (same design, mobile hidden) */}
        <div
          className={[
            "hidden sm:flex",
            "flex items-center gap-2 px-3 sm:px-4 py-2",
            menu?.length ? "bg-[#9481ce] shadow-md" : "",
            "rounded-full text-white text-sm relative",
            "max-w-[52vw] sm:max-w-none overflow-visible whitespace-nowrap",
            "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          ].join(" ")}
        >
          {menu?.length
            ? menu.map((item, i) => {
                if (item.children?.length) {
                  const isActive = parentActive(item.children);
                  return (
                    <DropdownPill
                      key={`parent-${item.title}`}
                      item={item}
                      idx={i}
                      isActive={isActive}
                      open={openIdx === i}
                      onOpen={setOpenIdx}
                      registerActiveRef={(el) => {
                        if (isActive) activeBtnRef.current = el;
                      }}
                    />
                  );
                }
                return (
                  <NavLink
                    key={item.link}
                    to={item.link}
                    end
                    className={({ isActive }) =>
                      [
                        "cursor-pointer px-3 py-1 text-xs rounded-md transition outline-none",
                        "focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#9481ce]",
                        "hover:bg-white/20",
                        isActive
                          ? "bg-white text-[#4b3d91] font-semibold shadow-sm"
                          : "text-white/95",
                      ].join(" ")
                    }
                    ref={(el) => {
                      if (!el) return;
                      if (el.getAttribute("aria-current") === "page")
                        activeBtnRef.current = el;
                    }}
                  >
                    <span className="whitespace-nowrap">{item.title}</span>
                  </NavLink>
                );
              })
            : null}
        </div>
      </div>

      {/* Right side: bell + user */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* <button
          type="button"
          onClick={handleExpandToggle}
          className="hidden md:block mr-2 sm:mr-4 grid place-items-center p-2 rounded-full hover:bg-violet-50 active:scale-[0.98]"
          title={isFs ? "Exit fullscreen" : "Fullscreen"}
          aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {screenType === "expanded" ? (
            <FiMinimize size={20} className="text-[#7d70b2]" />
          ) : (
            <IoMdExpand size={20} className="text-[#7d70b2]" />
          )}
        </button> */}

        {/* ✅ user box responsive */}
        <div className="flex items-center justify-between gap-2 md:border md:border-[#7d70b2]/50 rounded-[20px] bg-white/70 backdrop-blur-md px-2 py-1 w-full max-w-[520px] sm:max-w-[420px] md:max-w-[460px]">
          {/* Notification bell */}
          <div className="relative mr-3 md:mr-1">
            {unreadCountSel > 0 && (
              <p className="absolute -top-2 -right-2 bg-red-500 text-white py-[1px] px-[6px] text-[9px] rounded-full z-30">
                {badge}
              </p>
            )}
            {unreadCountSel > 0 ? (
              <motion.div
                className="ml-1 cursor-pointer"
                animate={{ rotate: [0, -15, 15, -10, 10, -5, 5, 0] }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  repeatDelay: 4,
                  ease: "easeInOut",
                }}
                onClick={openDropdown}
                title="Notifications"
              >
                <FaBell className="text-[#9481ce] w-4 h-4" />
              </motion.div>
            ) : (
              <FaBell
                className="text-[#9481ce] w-4 h-4  ml-1 cursor-pointer"
                onClick={openDropdown}
              />
            )}
          </div>

          {/* User info */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="hidden sm:block flex flex-col items-end leading-tight min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[11px] sm:text-xs font-semibold text-[#7d70b2] max-w-[150px] sm:max-w-[180px] truncate">
                  {user?.userName ||
                    (user?.email ? user?.email?.split("@")[0] : "Admin")}{" "}
                  {user?.employeeDetails?.employeeCode
                    ? "( " + user?.employeeDetails?.employeeCode + " )"
                    : ""}
                </span>
              </div>

              <span className="hidden sm:block text-[9px] sm:text-[10px] text-gray-400 font-medium max-w-[220px] truncate">
                {user?.email || "admin@gmail.com"}
              </span>
            </div>

            <div ref={avatarBtnRef} className="grid place-items-center">
              <FaUserCircle
                className="w-8 h-8 ml-2 text-[#9481ce] cursor-pointer"
                onClick={() => setShowMenu((s) => !s)}
                title="Profile menu"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Mobile Menu Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/30"
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileOpenParent(-1);
              }}
            />

            <motion.div
              initial={{ y: -10, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -10, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="fixed top-2 left-2 right-2 z-50 rounded-2xl overflow-hidden
                         bg-white/95 backdrop-blur-xl shadow-2xl ring-1 ring-violet-200"
            >
              <div className="px-3 py-3 bg-[#9481ce] text-white flex items-center justify-between">
                <div className="text-sm font-semibold">Menu</div>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setMobileOpenParent(-1);
                  }}
                  className="h-9 w-9 grid place-items-center rounded-full bg-white/20"
                  aria-label="Close menu"
                  title="Close"
                >
                  <FiX />
                </button>
              </div>

              <div className="max-h-[70vh] overflow-y-auto p-2">
                {(menu || []).map((item, idx) => {
                  const hasChildren = item?.children?.length > 0;

                  if (!hasChildren) {
                    return (
                      <NavLink
                        key={item.link}
                        to={item.link}
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setMobileOpenParent(-1);
                        }}
                        className={({ isActive }) =>
                          [
                            "block px-3 py-2 rounded-xl text-sm",
                            "hover:bg-violet-50 transition",
                            isActive
                              ? "text-violet-700 font-semibold"
                              : "text-gray-800",
                          ].join(" ")
                        }
                      >
                        {item.title}
                      </NavLink>
                    );
                  }

                  const open = mobileOpenParent === idx;

                  return (
                    <div key={`m-parent-${item.title}`} className="mb-1">
                      <button
                        type="button"
                        onClick={() => setMobileOpenParent(open ? -1 : idx)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-xl
                                   text-sm text-gray-800 hover:bg-violet-50 transition"
                      >
                        <span className="font-medium">{item.title}</span>
                        <FiChevronDown
                          className={`transition ${open ? "rotate-180" : ""}`}
                        />
                      </button>

                      <AnimatePresence>
                        {open && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.18 }}
                            className="pl-2 pr-1 overflow-hidden"
                          >
                            <div className="mt-1 space-y-1">
                              {item.children.map((child) => (
                                <NavLink
                                  key={child.link}
                                  to={child.link}
                                  onClick={() => {
                                    setMobileMenuOpen(false);
                                    setMobileOpenParent(-1);
                                  }}
                                  className={({ isActive }) =>
                                    [
                                      "flex items-center gap-2 px-3 py-2 rounded-xl text-sm",
                                      "hover:bg-violet-50 transition",
                                      isActive
                                        ? "text-violet-700 font-semibold"
                                        : "text-gray-700",
                                    ].join(" ")
                                  }
                                >
                                  <FiChevronRight className="text-gray-400" />
                                  {child.title}
                                </NavLink>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Notifications Dropdown */}
      <AnimatePresence>
        {showNotifications && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 5 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full right-2 sm:right-32 -mt-2 w-[92vw] sm:w-[420px] rounded-xl shadow-2xl bg-white/95 backdrop-blur-md ring-2 ring-violet-400 z-50 overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#7f6bd6] via-[#a88ad9] to-[#f2d6b3] text-white">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Notifications</span>
                {unreadCountSel > 0 && (
                  <span className="text-[10px] bg-white/20 px-2 py-[2px] rounded-full">
                    {badge} new
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setSoundOn((s) => {
                      const next = !s;
                      localStorage.setItem("notifSound", next ? "on" : "off");
                      return next;
                    })
                  }
                  className="p-2 rounded-full bg-white/20 hover:bg-white/30"
                  title={soundOn ? "Sound on" : "Sound off"}
                >
                  {soundOn ? <FiVolume2 /> : <FiVolumeX />}
                </button>

                <button
                  className="text-xs underline"
                  onClick={async () => {
                    try {
                      await markAllRead().unwrap();
                    } catch {}
                    dispatch(markAllReadLocal());
                  }}
                >
                  Mark all read
                </button>

                <button
                  className="text-lg leading-none"
                  onClick={() => setShowNotifications(false)}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto p-2">
              {notifications.length === 0 ? (
                <div className="px-4 py-10 text-center text-gray-500 text-sm">
                  No notifications yet.
                </div>
              ) : (
                notifications?.map((item) => {
                  const { Icon, bg } = pickNotifIcon(item);
                  return (
                    <button
                      key={item._id || `${item.name}-${item.time}`}
                      onClick={async () => {
                        try {
                          await markRead([item._id]).unwrap();
                          dispatch(markReadLocal([item._id]));
                        } catch {}
                        setShowNotifications(false);
                        if (item.url) navigate(item.url);
                      }}
                      className={[
                        "w-full text-left grid grid-cols-[44px_1fr_auto] items-center gap-3",
                        "px-3 py-3 rounded-lg mb-2 bg-white hover:bg-violet-50 transition",
                        "ring-1 ring-gray-100 hover:ring-violet-200",
                        item.unread
                          ? "relative before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-l-lg before:bg-emerald-400"
                          : "",
                      ].join(" ")}
                    >
                      <div
                        className={`w-10 h-10 rounded-full grid place-items-center ring-1 ring-white/60 shadow-sm bg-gradient-to-br ${bg}`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-800 truncate">
                            {item.name}
                          </div>
                          {item.unread && (
                            <span className="text-[10px] px-1.5 py-[1px] rounded bg-emerald-100 text-emerald-700">
                              new
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-700 line-clamp-2">
                          {renderHighlightWord(item, "Create Company")}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1">
                          {item.time}
                        </div>
                      </div>

                      {item.unread && (
                        <span className="w-2 h-2 rounded-full bg-emerald-500 justify-self-end" />
                      )}
                    </button>
                  );
                })
              )}
            </div>

            <div
              className="px-4 py-3 text-center text-sm text-[#4b3d91] font-semibold cursor-pointer hover:underline bg-white/80"
              onClick={() => {
                setShowNotifications(false);
                navigate("/notifications");
              }}
            ></div>

            {showEnableHint && (
              <div className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-200">
                Sound blocked by browser. Click anywhere once to enable.
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Dropdown */}
      <AnimatePresence>
        {showMenu && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 8, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="absolute top-12 right-2  sm:right-6 z-50 w-[92vw] sm:w-[280px] rounded-2xl overflow-hidden bg-white/85 backdrop-blur-xl shadow-2xl ring-1 ring-violet-200"
          >
            <div className="px-2 py-3 bg-gradient-to-r from-[#7f6bd6] via-[#a88ad9] to-[#f2d6b3] text-white">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-white/25 ring-1 ring-white/40 grid place-items-center">
                    <FaUserCircle className="h-8 w-8 text-white/95" />
                  </div>
                  <div className="min-w-0 ">
                    <div className="text-sm font-semibold truncate">
                      {user?.userName ||
                        (user?.email ? user?.email?.split("@")[0] : "Admin")}
                    </div>
                    <div className="text-[9px] text-white/85 truncate">
                      {user?.email || "admin@gmail.com"}
                    </div>
                  </div>
                </div>

                <div className="shrink-0">
                  <div className="inline-flex items-center gap-1.5 px-1.5 py-1 rounded-full bg-white/20 ring-1 ring-white/30">
                    <GiWallet className="text-white" />
                    <span className="text-[10px] font-semibold">
                      ₹{" "}
                      {user?.employeeDetails?.employeeCommission
                        ? twoDecimal(user?.employeeDetails?.employeeCommission)
                        : "0.00"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-1 py-1">
              {user?.role === "appowner" && (
                <MenuItem
                  icon={<FaUser className="text-[#7d70b2]" />}
                  label="Admin Dashboard"
                  onClick={() => {
                    setShowMenu(false);
                    navigate("/admin/tenant-list");
                  }}
                />
              )}
              <MenuItem
                icon={<FaUser className="text-[#7d70b2]" />}
                label="Profile"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/profile");
                }}
              />
              <MenuItem
                icon={<LuScanSearch size={24} className="text-[#7d70b2]" />}
                label="Scan Item"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/stocks/labelled-stock-scan");
                }}
              />
              <MenuItem
                icon={<FaRupeeSign className="text-[#7d70b2]" />}
                label="Daily Rate"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/masters/daily-rate-list");
                }}
              />
              <MenuItem
                icon={<IoPricetags className="text-[#7d70b2]" />}
                label="Label Design"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/designs/label/List");
                }}
              />
              <MenuItem
                icon={<FaWhatsapp className="text-[#25D366]" />}
                label="WhatsApp Commerce"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/whatsapp/orders");
                }}
              />

              {/* <MenuItem
                icon={<IoSparklesSharp className="text-[#7d70b2]" />}
                label="AI Marketing Post"
                hint="Generate daily shop posts"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/marketing/ai-post-generator");
                }}
              /> */}
              {/* <MenuItem
                icon={<FaUsersCog className="text-[#7d70b2]" />}
                label="Management"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/management");
                }}
              /> */}

              {user?.role != "owner" ? (
                <MenuItem
                  icon={<GiWallet className="text-[#7d70b2]" />}
                  label="Redeem Commission"
                  hint={`₹ ${
                    user?.employeeDetails?.employeeCommission
                      ? twoDecimal(user?.employeeDetails?.employeeCommission)
                      : "0.00"
                  } available`}
                  onClick={() => {
                    setShowMenu(false);
                    setRedeemOpen(true);
                  }}
                />
              ) : (
                ""
              )}

              <MenuItem
                icon={<FaFileAlt className="text-[#7d70b2]" />}
                label="Estimates"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/estimate/estimate-list");
                }}
              />
               <MenuItem
                icon={<FaBullhorn className="text-[#7d70b2]" />}
                label="Marketing"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/marketing/daily-rate-message");
                }}
              />
              {/* <MenuItem
                icon={
                  isGeneratingPin ? (
                    <FiLoader className="animate-spin text-[#7d70b2]" />
                  ) : (
                    <FaComment className="text-[#7d70b2]" />
                  )
                }
                label={
                  isGeneratingPin
                    ? "Generating Security Pin..."
                    : "Generate Security Pin"
                }
                onClick={async () => {
                  if (isGeneratingPin) return;
                  setShowMenu(false);
                  await onGenerate();
                }}
                disabled={isGeneratingPin}
              /> */}

              <div className="my-1 h-px bg-violet-100" />

              <MenuItem
                danger
                icon={<FaLock className="text-rose-600" />}
                label="Logout"
                onClick={async () => {
                  try {
                    await logoutUser().unwrap();
                    dispatch(clearUser());
                    window.location.replace("/login");
                  } catch (err) {
                    console.error("Logout failed", err);
                  }
                  setShowMenu(false);
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ---------- small reusable menu item ---------- */
function MenuItem({
  icon,
  label,
  hint,
  onClick,
  danger = false,
  disabled = false,
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={[
        "w-full flex items-center gap-3 px-1 py-1 rounded-xl text-left transition",
        "hover:bg-violet-50 active:scale-[0.99]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300",
        disabled
          ? "opacity-60 cursor-not-allowed hover:bg-transparent active:scale-100"
          : "",
        danger ? "text-rose-700 hover:bg-rose-50" : "text-gray-800",
      ].join(" ")}
    >
      <div
        className={[
          "h-9 w-9 rounded-lg grid place-items-center",
          danger ? "bg-rose-50" : "bg-violet-50",
        ].join(" ")}
      >
        {icon}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {hint ? (
          <div className="text-[11px] text-gray-500 truncate">{hint}</div>
        ) : null}
      </div>

      <span className="text-gray-400">›</span>
    </button>
  );
}
